import { useEffect, useRef } from "react";

/**
 * Lecteur code-barres intégré (Zebra TC21 / DataWedge en « sortie clavier »)
 * ou douchette USB / Bluetooth : le lecteur « tape » le code très vite, suivi
 * en général d'Entrée. On reconnaît cette rafale n'importe où dans la page,
 * sans avoir à cliquer d'abord dans la barre de saisie (et sans ouvrir le
 * clavier du téléphone).
 *
 * - Hors champ de saisie : les caractères de la rafale sont captés, puis
 *   onScan(code) est appelé (sur Entrée / Tab, ou après un court silence si le
 *   lecteur n'envoie pas d'Entrée).
 * - Dans un champ marqué data-douchette : le texte arrive normalement, mais la
 *   rafale est aussi transmise à onScan (qui remplace le contenu du champ).
 * - Dans les autres champs (nom d'une liste, commentaire…) : rien n'est capté.
 */
const ECART_MAX_MS = 50;   // entre deux caractères d'une rafale de lecteur
const SILENCE_MS = 150;    // fin de code sans Entrée
const LONGUEUR_MIN = 4;   // codes de chariot courts (ex. « CHA1 »)

// Heure de la dernière touche reçue : on ne déplace pas le curseur au milieu
// d'un scan (sinon le code serait coupé en deux).
let derniereTouche = 0;

// Menus déroulants / listes ouvertes (Radix Select, menu…) : la frappe au
// clavier y sert à choisir un élément (ex. taper « 415 » choisit la tournée
// 415) ; on n'y touche pas.
const MENU = "[role=listbox], [role=menu], [role=option], [role=menuitem], [role=combobox]";
const menuOuvert = () => typeof document !== "undefined" && !!document.querySelector("[role=listbox], [role=menu]");
if (typeof window !== "undefined") {
  window.addEventListener("keydown", () => { derniereTouche = performance.now(); }, true);
}

export function useDouchette(onScan, actif = true) {
  const ref = useRef(onScan);
  ref.current = onScan;
  useEffect(() => {
    if (!actif) return undefined;
    let buf = "";
    let dernier = 0;
    let minuterie = null;
    let champ = null;   // champ data-douchette qui reçoit la rafale

    const terminer = (ev) => {
      clearTimeout(minuterie);
      const code = buf;
      buf = "";
      if (code.length < LONGUEUR_MIN) return false;
      if (ev) { ev.preventDefault(); ev.stopPropagation(); }
      ref.current(code.trim());
      return true;
    };

    const onKey = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey || e.isComposing) return;
      const t = e.target;
      const editable = !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""));
      const cible = editable && t.closest ? t.closest("[data-douchette]") : null;
      if (editable && !cible) { buf = ""; return; }
      if ((t && t.closest && t.closest(MENU)) || menuOuvert()) { buf = ""; return; }
      const maintenant = performance.now();
      if (e.key === "Enter" || e.key === "Tab") {
        if (buf && maintenant - dernier < SILENCE_MS * 2) terminer(e);
        else buf = "";
        return;
      }
      if (e.key.length !== 1) return;
      if (maintenant - dernier > ECART_MAX_MS) buf = "";   // nouvelle frappe (humaine ou nouveau code)
      buf += e.key;
      dernier = maintenant;
      champ = cible;
      // Le 1er caractère n'est pas bloqué (frappe humaine : raccourcis, saisie
      // au clavier) ; ensuite, dans une rafale de lecteur, on bloque.
      if (!editable && buf.length > 1) e.preventDefault();
      clearTimeout(minuterie);
      // Sans Entrée : hors champ seulement (dans un champ, c'est useRafale qui valide).
      minuterie = setTimeout(() => { if (buf.length >= LONGUEUR_MIN && !champ) terminer(null); else buf = ""; }, SILENCE_MS);
    };

    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); clearTimeout(minuterie); };
  }, [actif]);
}

/**
 * Rafale de saisie dans un champ : le lecteur Zebra (DataWedge) écrit souvent
 * le code d'un bloc, sans touche Entrée et sans évènements clavier lisibles.
 * On regarde donc le texte du champ : s'il a grandi d'au moins 4 caractères
 * presque d'un coup, c'est un scan -> onRafale() après un court silence.
 * Une frappe humaine (un caractère à la fois, plus lentement) n'est pas validée.
 * Renvoie une fonction à appeler à chaque changement : (ancien, nouveau).
 * onRafale(code) reçoit seulement le texte écrit par le lecteur (si le champ
 * contenait déjà un ancien code, il n'est pas recollé devant).
 */
export function useRafale(onRafale, silenceMs = 120) {
  const etat = useRef({ dernier: 0, nb: 0, t: null, base: "", texte: "" });
  const cb = useRef(onRafale);
  cb.current = onRafale;
  useEffect(() => () => clearTimeout(etat.current.t), []);
  const changer = (ancien, nouveau) => {
    const s = etat.current;
    const maintenant = performance.now();
    const ajout = (nouveau || "").length - (ancien || "").length;
    if (maintenant - s.dernier > 60) { s.nb = 0; s.base = ancien || ""; }
    s.nb += Math.max(ajout, 0);
    s.dernier = maintenant;
    s.texte = nouveau || "";
    clearTimeout(s.t);
    if (s.nb >= LONGUEUR_MIN) {
      s.t = setTimeout(() => {
        s.nb = 0;
        cb.current(codeDe(s));
      }, silenceMs);
    }
  };
  const codeDe = (s) => (s.base && s.texte.startsWith(s.base) ? s.texte.slice(s.base.length) : s.texte).trim();
  /** rafale en cours (lecteur en train d'écrire) : son code, sinon null */
  changer.enCours = () => (etat.current.nb >= LONGUEUR_MIN ? codeDe(etat.current) : null);
  changer.annuler = () => { clearTimeout(etat.current.t); etat.current.nb = 0; };
  return changer;
}

/** Garde le curseur dans un champ (après un clic ailleurs, une fenêtre fermée…). */
export function useGarderFocus(ref, actif = true) {
  useEffect(() => {
    if (!actif) return undefined;
    const reprendre = () => {
      if (performance.now() - derniereTouche < 200) { setTimeout(reprendre, 200); return; }
      const a = document.activeElement;
      const ailleurs = a && a !== document.body && a !== ref.current && (/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) || a.isContentEditable);
      const fenetre = document.querySelector("[role=dialog], [role=alertdialog], [role=listbox], [role=menu]");
      if (!ailleurs && !fenetre) ref.current?.focus({ preventScroll: true });
    };
    const plusTard = () => setTimeout(reprendre, 30);
    const t = setTimeout(reprendre, 100);
    const el = ref.current;
    document.addEventListener("click", plusTard, true);
    document.addEventListener("visibilitychange", plusTard);
    el?.addEventListener("blur", plusTard);
    const veille = setInterval(reprendre, 1500);   // filet de sécurité (fenêtre fermée, etc.)
    return () => {
      clearTimeout(t); clearInterval(veille);
      document.removeEventListener("click", plusTard, true);
      document.removeEventListener("visibilitychange", plusTard);
      el?.removeEventListener("blur", plusTard);
    };
  }, [actif, ref]);
}

export const ECRAN_TACTILE = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
